import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Youtube, Linkedin, Instagram } from 'lucide-react';
import { businessInfo, navigation, footer } from '../../config/content';
import { images } from '../../config/assets';
import Container from '../ui/Container';

const Footer: React.FC = () => {
  return (
    <footer className="text-white">
      {/* Main Footer - Dark Navy Background */}
      <div style={{ backgroundColor: '#1E2547' }} className="border-b border-white/10">
        <Container>
          <div className="flex flex-col lg:flex-row gap-12 py-12">
            {/* Column 1 - Company Info (40% on desktop) */}
            <div className="lg:w-[40%] lg:border-r border-white/10 lg:pr-12">
              {/* Logo */}
              <img
                src={images.logo}
                alt={businessInfo.name}
                className="w-1/2 lg:w-1/2 mb-6"
                style={{ filter: 'brightness(0) invert(1)' }}
              />

              {/* Description */}
              <p className="text-white/90 text-sm leading-relaxed mb-6 lg:pr-8">
                {footer.description}
              </p>

              {/* Social Icons */}
              <div className="flex gap-2.5">
                <a
                  href={businessInfo.social.facebook || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: '#262F59' }}
                >
                  <Facebook size={20} className="text-white" />
                </a>
                <a
                  href={businessInfo.social.twitter || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: '#262F59' }}
                >
                  <Twitter size={20} className="text-white" />
                </a>
                <a
                  href={businessInfo.social.youtube || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: '#262F59' }}
                >
                  <Youtube size={20} className="text-white" />
                </a>
                <a
                  href={businessInfo.social.linkedin || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: '#262F59' }}
                >
                  <Linkedin size={20} className="text-white" />
                </a>
                <a
                  href={businessInfo.social.instagram || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: '#262F59' }}
                >
                  <Instagram size={20} className="text-white" />
                </a>
              </div>
            </div>

            {/* Column 2 - Navigation & Contact (60% on desktop) */}
            <div className="lg:w-[60%] flex flex-col md:flex-row gap-12">
              {/* Quick Services Links */}
              <div className="md:w-1/2">
                <h4
                  className="text-lg font-semibold mb-4"
                  style={{ color: '#DCE3FF' }}
                >
                  Quick Services
                </h4>
                <ul className="space-y-2.5">
                  {navigation.footer.quickLinks.map((link) => (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        className="text-white/90 hover:text-white transition-colors text-base"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Information */}
              <div className="md:w-1/2 space-y-6">
                {/* Email */}
                <div>
                  <h3 className="text-sm font-normal mb-1" style={{ color: '#DCE3FF' }}>
                    WANTS TO CONNECT?
                  </h3>
                  <a
                    href={`mailto:${businessInfo.contact.email}`}
                    className="text-base font-semibold text-white hover:opacity-80 transition-opacity"
                  >
                    {businessInfo.contact.email}
                  </a>
                </div>

                {/* Phone */}
                <div>
                  <h3 className="text-sm font-normal mb-1" style={{ color: '#DCE3FF' }}>
                    CALL US AT
                  </h3>
                  <a
                    href={`tel:${businessInfo.contact.phone}`}
                    className="text-base font-semibold text-white hover:opacity-80 transition-opacity"
                  >
                    {businessInfo.contact.phone}
                  </a>
                </div>

                {/* Location */}
                <div>
                  <h3 className="text-sm font-normal mb-1" style={{ color: '#DCE3FF' }}>
                    OUR LOCATION
                  </h3>
                  <p className="text-base font-semibold text-white">
                    {businessInfo.contact.address.street},<br />
                    {businessInfo.contact.address.city}, {businessInfo.contact.address.province}, {businessInfo.contact.address.postalCode}
                  </p>
                </div>

                {/* Business Hours */}
                <div>
                  <h3 className="text-sm font-normal mb-1" style={{ color: '#DCE3FF' }}>
                    BUSINESS HOURS
                  </h3>
                  <p className="text-base font-semibold text-white leading-relaxed">
                    {businessInfo.hours.weekdays}<br />
                    {businessInfo.hours.saturday}<br />
                    {businessInfo.hours.sunday}<br />
                    <br />
                    <span style={{ color: '#23A654' }}>{businessInfo.hours.dispatch}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Legal bar */}
      <div style={{ backgroundColor: '#0F1426' }}>
        <Container>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-4 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <div>
              © {new Date().getFullYear()} {businessInfo.name}. All rights reserved.
            </div>
            <div className="flex gap-5">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/track" className="hover:text-white transition-colors">Track an Order</Link>
              <button
                type="button"
                onClick={() => {
                  // Clear the consent flag so CookieConsent shows the
                  // banner again on next mount / reload.
                  try { localStorage.removeItem('speedyship_consent'); } catch { /* ignore */ }
                  window.location.reload();
                }}
                className="hover:text-white transition-colors"
                style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit' }}
              >
                Cookie Preferences
              </button>
            </div>
          </div>
        </Container>
      </div>

    </footer>
  );
};

export default Footer;
